/**
 * Month ordering utilities for BreakdownTable
 * Handles chronological ordering of time-based dimensions
 */

import type { BreakdownCell } from '@/components/common/breakdown-tables/types';

/**
 * Standard month order (chronological)
 */
export const MONTH_ORDER = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Extract month name from a column string
 * Handles formats: "Jan", "Jan 2025", "Jan - Supplier A", "Jan 2025 - Supplier A"
 */
function extractMonth(column: string): string | null {
  const parts = column.split(' - ');
  const monthPart = parts[0].trim();

  // Extract month (remove year if present)
  const monthMatch = monthPart.match(
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/i,
  );
  if (monthMatch) {
    // Capitalize first letter, lowercase rest
    const month = monthMatch[1];
    return month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
  }

  return null;
}

/**
 * Extract year from a column string
 * Returns undefined if no year is present
 */
function extractYear(column: string): number | undefined {
  const parts = column.split(' - ');
  const monthPart = parts[0].trim();
  const yearMatch = monthPart.match(/\d{4}/);
  return yearMatch ? parseInt(yearMatch[0], 10) : undefined;
}

/**
 * Check if a column is a delta column (contains delta symbol)
 */
function isDeltaColumn(column: string): boolean {
  const parts = column.split(' - ');
  const monthPart = parts[0].trim();
  return monthPart.includes('Δ');
}

/**
 * Generate chronological order for columns that contain month data
 * Handles both "Jan" and "Jan 2025" formats
 * Returns an array sorted chronologically: ["Jan 2024", "Feb 2024", ..., "Jan 2025", ...]
 */
export function generateMonthColumnOrder(cells: BreakdownCell[]): string[] {
  // Extract all unique column values
  const columnSet = new Set<string>();
  cells.forEach((cell) => {
    columnSet.add(cell.column);
  });

  // Parse columns into { month, year, isDelta, original } format
  const parsedColumns = Array.from(columnSet)
    .map((col) => ({
      original: col,
      month: extractMonth(col),
      year: extractYear(col),
      isDelta: isDeltaColumn(col),
    }))
    .filter((p) => p.month !== null) as Array<{
    original: string;
    month: string;
    year: number | undefined;
    isDelta: boolean;
  }>;

  // Sort: by month first, then by year/delta within each month
  // Order within month: current year (highest), previous year, delta
  parsedColumns.sort((a, b) => {
    // First compare months (chronological order)
    const monthAIndex = MONTH_ORDER.indexOf(
      a.month as (typeof MONTH_ORDER)[number],
    );
    const monthBIndex = MONTH_ORDER.indexOf(
      b.month as (typeof MONTH_ORDER)[number],
    );
    if (monthAIndex !== monthBIndex) {
      return monthAIndex - monthBIndex;
    }

    // Same month - now sort by year/delta
    // Delta columns come last (order: 2)
    if (a.isDelta && !b.isDelta) return 1;
    if (!a.isDelta && b.isDelta) return -1;

    // Both are year columns - sort by year (higher year first, which is current year)
    if (!a.isDelta && !b.isDelta) {
      const yearA = a.year ?? 0;
      const yearB = b.year ?? 0;
      // Higher year (current year) comes first
      return yearB - yearA;
    }

    // Both are delta (shouldn't happen, but handle it)
    return 0;
  });

  return parsedColumns.map((p) => p.original);
}

/**
 * Generate chronological order for sub-columns that contain month data
 * Similar to generateMonthColumnOrder but for subColumn values
 */
export function generateMonthSubColumnOrder(cells: BreakdownCell[]): string[] {
  // Extract all unique subColumn values (excluding null)
  const subColumnSet = new Set<string>();
  cells.forEach((cell) => {
    if (cell.subColumn !== null && cell.subColumn !== undefined) {
      subColumnSet.add(cell.subColumn);
    }
  });

  // Parse subColumns into { month, year, isDelta, original } format
  const parsedSubColumns = Array.from(subColumnSet)
    .map((col) => ({
      original: col,
      month: extractMonth(col),
      year: extractYear(col),
      isDelta: isDeltaColumn(col),
    }))
    .filter((p) => p.month !== null) as Array<{
    original: string;
    month: string;
    year: number | undefined;
    isDelta: boolean;
  }>;

  // Sort: by month first, then by year/delta within each month
  // Order within month: current year (highest), previous year, delta
  parsedSubColumns.sort((a, b) => {
    // First compare months (chronological order)
    const monthAIndex = MONTH_ORDER.indexOf(
      a.month as (typeof MONTH_ORDER)[number],
    );
    const monthBIndex = MONTH_ORDER.indexOf(
      b.month as (typeof MONTH_ORDER)[number],
    );
    if (monthAIndex !== monthBIndex) {
      return monthAIndex - monthBIndex;
    }

    // Same month - now sort by year/delta
    // Delta columns come last (order: 2)
    if (a.isDelta && !b.isDelta) return 1;
    if (!a.isDelta && b.isDelta) return -1;

    // Both are year columns - sort by year (higher year first, which is current year)
    if (!a.isDelta && !b.isDelta) {
      const yearA = a.year ?? 0;
      const yearB = b.year ?? 0;
      // Higher year (current year) comes first
      return yearB - yearA;
    }

    // Both are delta (shouldn't happen, but handle it)
    return 0;
  });

  return parsedSubColumns.map((p) => p.original);
}
